import {
  createContext, useContext, useState, useCallback, useEffect, useMemo,
  type ReactNode,
} from "react"
import {
  type SupportedLanguage,
  getStoredLanguage, setStoredLanguage, fetchTranslations,
} from "@/lib/i18n"

interface I18nContextValue {
  language: SupportedLanguage
  setLanguage: (lang: SupportedLanguage) => void
  t: (key: string, fallback?: string) => string
  isLoading: boolean
}

const I18nContext = createContext<I18nContextValue | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLang] = useState<SupportedLanguage>(getStoredLanguage)
  const [translations, setTranslations] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(true)

  const loadTranslations = useCallback(async (lang: SupportedLanguage) => {
    setIsLoading(true)
    try {
      const data = await fetchTranslations(lang)
      setTranslations(data)
    } catch {
      // API'den çekilemezse boş kalır, fallback kullanılır
      setTranslations({})
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadTranslations(language)
  }, [language, loadTranslations])

  // Update html lang attribute
  useEffect(() => {
    document.documentElement.lang = language
  }, [language])

  const setLanguage = useCallback((lang: SupportedLanguage) => {
    setStoredLanguage(lang)
    setLang(lang)
  }, [])

  const t = useCallback(
    (key: string, fallback?: string): string => {
      return translations[key] || fallback || key
    },
    [translations],
  )

  const value = useMemo(
    () => ({ language, setLanguage, t, isLoading }),
    [language, setLanguage, t, isLoading],
  )

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  )
}

export function useI18n() {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error("useI18n must be used within I18nProvider")
  return ctx
}
